import { getRoleNameFromRoleEnum } from '@helpers/functions';
import { clearCustomFormAnswers } from '@therame/ui-domain';
import {
  Country,
  GERMANY,
  OfficeLocationFullSizeDto,
  OfficeProductFullSizeDto,
  Role,
  UserFullSizeDto,
} from '@therame/shared';
import { ref, type Ref } from 'vue';
import type { CustomFormDraft } from '@/composables/useCustomFormEditor';
import { idKindOf, type SidebarType } from '@/registries/sidebars';
import { useAuthStore } from '@/stores/authStore';
import { useOfficeLocationStore } from '@/stores/officeLocationStore';
import {
  activeDraftKey,
  draftKeyKind,
  draftKeyType,
  identityDraftKey,
  identityDraftKeyParts,
  type DraftKey,
} from '@/stores/draftKey';
import {
  createAddExerciseWizard,
  type AddExerciseWizard,
} from '@/stores/exerciseLibrary/addExerciseWizard';
import { useDraftStateStore } from '@/stores/draftStateStore';

/**
 * One create-flow that can hold unsaved work (TM-9455).
 *
 * Lives under `stores/` rather than `registries/` because every entry binds a
 * type to its Pinia store, and the boundaries rule keeps registries declarative
 * — a registry that reaches into state would invert the layering. This is still
 * the single place a new draft type is announced.
 *
 * A draft is registered here rather than reported by the panel, because the two
 * places that need to know about it — the collapsed rail and the menu dot — both
 * render while the panel is unmounted, on pages the panel has never been on. The
 * definition therefore has to reach the data without a component in the middle,
 * which means going through the type's own Pinia store.
 *
 * The store lookups sit INSIDE the callbacks on purpose: `defineStore` only
 * builds the store on first call, so registering a type costs nothing until
 * something actually asks about it.
 */
/**
 * What an Entwurf of each type CONTAINS — the return shape of its {@link
 * DraftDefinition.create} factory, indexed by type so `useDraftForm('inviteUser', …)`
 * hands the panel a typed buffer without a cast (ADR-0029 §H-1).
 *
 * A type absent from here gets `object` back from the seam, which is the compile-time
 * nudge to declare it: the registry is meant to be the complete declaration of what a
 * draft of a type holds, and a factory nobody can read the shape of is only half of one.
 */
export interface DraftBufferShapes {
  inviteUser: InviteUserBuffer;
  createPatient: CreatePatientBuffer;
  addOfficeLocation: AddOfficeLocationBuffer;
  officeProductDetails: OfficeProductBuffer;
  customFormDetail: CustomFormDetailBuffer;
  customFormPlayer: CustomFormPlayerBuffer;
  addExercise: AddExerciseWizard;
}

/** The four entered values of an invitation (ADR-0029 §H-1). */
export interface InviteUserBuffer {
  email: Ref<string>;
  role: Ref<string>;
  approval: Ref<boolean>;
  selectedOfficeLocations: Ref<OfficeLocationFullSizeDto[]>;
}

/**
 * Declared here rather than in the panel so a test — and the survives-a-remount
 * harness — can construct an invitation's buffer without mounting the form.
 *
 * Reads the acting user's office and the office-location list from their stores, the
 * same two the panel used to close over.
 */
function createInviteUserBuffer(): InviteUserBuffer {
  const currentOfficeLocation = useAuthStore().authContext?.user.currentOfficeLocation;
  const allOfficeLocations = useOfficeLocationStore().allOfficeLocations;
  return {
    email: ref<string>(''),
    role: ref<string>(getRoleNameFromRoleEnum(Role.THERAPIST)),
    approval: ref<boolean>(false),
    // Seeded from the acting user's office at creation, so it is part of the baseline
    // rather than a change — and a restored draft keeps whatever the user selected.
    selectedOfficeLocations: ref<OfficeLocationFullSizeDto[]>(
      currentOfficeLocation
        ? allOfficeLocations.filter((ol) => ol.id === currentOfficeLocation.id)
        : allOfficeLocations,
    ),
  };
}

/**
 * What a new patient's Entwurf holds (ADR-0029 §H-1): twelve fields the user types,
 * picks or ticks, plus the Praxisleitung and the Standort, which arrive from queries
 * after this runs and are absorbed through the seam's `seed` rather than counted.
 */
export interface CreatePatientBuffer {
  salutation: Ref<string>;
  firstname: Ref<string>;
  lastname: Ref<string>;
  /** `YYYY-MM-DD`, as the date input speaks it; empty when not given. */
  birthdate: Ref<string>;
  email: Ref<string>;
  phone: Ref<string>;
  street: Ref<string>;
  zip: Ref<string>;
  town: Ref<string>;
  krankenkasse: Ref<string>;
  approval: Ref<boolean>;
  sendInvite: Ref<boolean>;
  selectedStaff: Ref<UserFullSizeDto | null>;
  selectedOfficeLocation: Ref<OfficeLocationFullSizeDto | null>;
}

/**
 * Declared here rather than in the panel so a test — and the survives-a-remount
 * harness — can construct a patient's buffer without mounting the 550-line form.
 *
 * The Praxisleitung and the Standort both default from a query, i.e. after this runs,
 * so the panel absorbs those through the seam's `seed` rather than seeding them here
 * (§B-ter).
 */
function createPatientBuffer(props: Record<string, unknown>): CreatePatientBuffer {
  // The patient search's no-match action carries the text the user typed there.
  // Per PRD #944 we do NOT parse it (no first/last split, no phone heuristics) — the
  // raw string lands in `lastname` so a German receptionist's typical "Müller" /
  // "Müller, Anna" flow needs zero rework. Seeded HERE, in the factory, so it is part
  // of the baseline rather than a change, and a restored draft keeps what the user has
  // since typed (§B-ter: a seed inside the factory needs no absorber, because the
  // factory runs exactly once).
  const initialName = typeof props.initialName === 'string' ? props.initialName.trim() : '';
  return {
    salutation: ref<string>(''),
    firstname: ref<string>(''),
    lastname: ref<string>(initialName),
    birthdate: ref<string>(''),
    email: ref<string>(''),
    phone: ref<string>(''),
    street: ref<string>(''),
    zip: ref<string>(''),
    town: ref<string>(''),
    krankenkasse: ref<string>(''),
    approval: ref<boolean>(false),
    sendInvite: ref<boolean>(true),
    selectedStaff: ref<UserFullSizeDto | null>(new UserFullSizeDto()),
    selectedOfficeLocation: ref<OfficeLocationFullSizeDto | null>(null),
  };
}

/**
 * What a new Standort's Entwurf holds (ADR-0029 §H-1).
 *
 * It used to be ~30 fields in a Pinia store, i.e. one form for the whole app;
 * `useOfficeLocationForm` wraps this buffer with the validation and the submit around
 * it. Everything that OUTLIVES a remount lives here — which is the entered values, plus
 * the Kürzel (derived from the name by a watcher, and overridable, so it carries the
 * `abbreviationChangedByHand` flag with it) and the Praxisleitung (seeded from a
 * query). The validation flags and the progress spinners stay local to the composable
 * instead: a remount should start with a clean slate rather than replay errors the user
 * has since fixed.
 */
export interface AddOfficeLocationBuffer {
  officeName: Ref<string>;
  abbreviation: Ref<string>;
  officeStreet: Ref<string>;
  officeZipcode: Ref<string>;
  officeTown: Ref<string>;
  officeCountry: Ref<Country>;
  usesSeparateInvoiceAddress: Ref<boolean>;
  officeCompanyNameForInvoice: Ref<string>;
  officeStreetForInvoice: Ref<string>;
  officeZipcodeForInvoice: Ref<string>;
  officeTownForInvoice: Ref<string>;
  officeCountryForInvoice: Ref<string>;
  officePhone: Ref<string>;
  email: Ref<string>;
  selectedStaff: Ref<UserFullSizeDto | null>;
  abbreviationChangedByHand: Ref<boolean>;
}

/** @see AddOfficeLocationBuffer */
function createAddOfficeLocationBuffer(): AddOfficeLocationBuffer {
  return {
    officeName: ref<string>(''),
    abbreviation: ref<string>(''),
    officeStreet: ref<string>(''),
    officeZipcode: ref<string>(''),
    officeTown: ref<string>(''),
    officeCountry: ref<Country>(GERMANY),

    usesSeparateInvoiceAddress: ref<boolean>(false),
    officeCompanyNameForInvoice: ref<string>(''),
    officeStreetForInvoice: ref<string>(''),
    officeZipcodeForInvoice: ref<string>(''),
    officeTownForInvoice: ref<string>(''),
    officeCountryForInvoice: ref<string>('DE'),

    officePhone: ref<string>(''),
    email: ref<string>(''),
    // None until the panel seeds the current user. An empty DTO here counted as chosen:
    // the seed never ran and the form sent `ownerId: undefined`.
    selectedStaff: ref<UserFullSizeDto | null>(null),
    abbreviationChangedByHand: ref<boolean>(false),
  };
}

/**
 * What an Entwurf of a new Leistung holds (ADR-0029 §H-1) — the product itself, the
 * picture's preview, and the picked file that uploads after „Leistung anlegen". A saved
 * Leistung is no draft: it saves each value on its own (ADR-0048).
 */
export interface OfficeProductBuffer {
  officeProduct: Ref<OfficeProductFullSizeDto>;
  imageUrl: Ref<string | undefined>;
  updatedImage: Ref<File | null>;
}

/** @see OfficeProductBuffer */
function createOfficeProductBuffer(): OfficeProductBuffer {
  return {
    officeProduct: ref<OfficeProductFullSizeDto>(new OfficeProductFullSizeDto()),
    imageUrl: ref<string | undefined>(undefined),
    updatedImage: ref<File | null>(null),
  };
}

/**
 * What an Entwurf of a Formular holds (ADR-0029 §H-1): the staged form, and nothing
 * else — the editor's section tabs, its validation and its upload spinners are chrome
 * that a remount should start over rather than replay.
 *
 * `null` is what `useStagedEdit` reads as "this buffer is new, stage the initial value
 * into it". Anything else is work restored from a previous mount, which it adopts
 * rather than overwrites — so the empty form is built by the editor, not here.
 */
export interface CustomFormDetailBuffer {
  form: Ref<CustomFormDraft | null>;
}

/** @see CustomFormDetailBuffer */
function createCustomFormDetailBuffer(): CustomFormDetailBuffer {
  return { form: ref<CustomFormDraft | null>(null) };
}

/**
 * What an Entwurf of a half-answered questionnaire holds — the STEP, and nothing else
 * (ADR-0029 §G-bis).
 *
 * The one draft whose user input does not live in its buffer at all: the player writes
 * its answers to its own `localStorage`, keyed by form and patient, so they survive even
 * a reload. What that storage never kept is WHICH SECTION the user was on, and coming
 * back to a half-answered form at question one is close enough to starting over. So the
 * buffer holds exactly the gap, and the draft's dirtiness is read from the answers.
 */
export interface CustomFormPlayerBuffer {
  sectionIndex: Ref<number>;
}

/** @see CustomFormPlayerBuffer */
function createCustomFormPlayerBuffer(): CustomFormPlayerBuffer {
  return { sectionIndex: ref<number>(0) };
}

export interface DraftDefinition {
  /** The sidebar whose `new`-sentinel column is this draft. */
  type: SidebarType;
  /**
   * This type's buffer: the object a panel of it keeps its entered values in, built
   * fresh for a draft nobody has started yet (ADR-0029 §H-1).
   *
   * Declared here rather than inside the panel's `<script setup>` so the registry is
   * the complete statement of what an Entwurf of this type contains — and so a test
   * can construct one without mounting a 200-to-1500-line form. Given the same bound
   * props {@link keyFor} gets, which is all a parked draft has.
   *
   * Absent for a type whose shape the registry cannot see from a column's bound props:
   * `prescription`'s depends on the payer domain the user picks, so its factory stays
   * in the payer bodies. Also absent for the drafts that are not buffer-backed at all.
   */
  create?: (props: Record<string, unknown>) => object;
  /**
   * This type lives in the DRAFT ZONE (ADR-0029 §G): a page may hold several of
   * its create panels at once, to the right of the cascade columns and outside the
   * URL, each its own draft. Its create flow still STARTS as the one active `:new`
   * cascade column and graduates into the zone when it would otherwise leave — see
   * `draftStore.graduateActiveColumn`.
   *
   * Absent means the original one-draft-per-type behaviour: a single `:new` column
   * in the URL, keyed by the bare type. The two coexist deliberately while the
   * remaining create flows migrate — the eight that have not are 200–1500 lines of
   * form each, and moving them is a per-panel job (see §B-ter on seeding).
   */
  zone?: boolean;
  /**
   * What a PARKED draft of this type is called, when the registry title would not
   * say it.
   *
   * A type whose registry title names the saved record needs the create side's name
   * stated — `officeProductDetails` is „Leistung", its draft „Leistung anlegen". A
   * parked draft is usually a CREATE, and its rail cannot ask the panel which it is:
   * there is no panel mounted, which is the whole point of a rail. So the create-side
   * name is stated here instead. Absent means the registry title already says it.
   *
   * A function when one type parks both kinds: `customFormDetail` holds create drafts
   * AND edit drafts of saved forms, and a single string would label half of them
   * wrongly. It is given the instance's frozen props, which is all a parked draft has.
   */
  draftTitle?: string | ((props: Record<string, unknown>) => string);
  /**
   * IDENTIFIED type: its columns are told apart by their own props rather than by
   * the `:new` sentinel, so several can be open at once — each already a distinct
   * panel, not successive generations of one create flow (ADR-0029 §G-bis).
   *
   * `customFormPlayer` is the shape: a filled-out questionnaire is not a create
   * flow. Its column carries a real form id, any number of forms can be open for
   * any number of patients, and there is nothing for a "Neu" click to replace — the
   * only way one is lost is by leaving the page, so identity, not generation, is
   * what a draft of it hangs on.
   *
   * Returns the draft key for a column with these props, or `null` when they do not
   * identify one (an anonymous open with no patient, where the answers cannot be
   * told apart either). Absent means the type keys its state by the bare type.
   */
  keyFor?: (props: Record<string, unknown>) => DraftKey | null;
  /**
   * This type has NO create flow: every column of it is identified, so there is no
   * single active `:new` column to graduate when the user leaves the page.
   *
   * Stated rather than inferred from `keyFor`, because a type can be both.
   * `customFormDetail` creates a new form in the one `:new` column AND edits saved
   * ones by identity; reading "has a keyFor" as "has no create flow" left its create
   * draft ungraduated — it vanished on a page change and came back only when the next
   * "Neu" happened to pick the buffer up again.
   */
  identifiedOnly?: boolean;
  /**
   * Has the user put anything in it yet, when {@link bufferBackedIsDirty} is not the
   * answer? A pristine create column is NOT a draft: it closes on the ✕ without a
   * prompt and leaves no menu dot, so a mis-click on "Neu" costs nothing.
   *
   * ABSENT is the normal case, and since RS-014 it is every case: a draft's unsaved
   * work is the difference between its buffer and its baseline, and a type that
   * declares a buffer has already said everything needed to answer this. Nine entries
   * spelled out the same one-liner, which is nine chances to spell it differently.
   *
   * Declare one only for a type whose user input does not live in its buffer at all —
   * and note that `customFormPlayer`, the one type that fits that description, still
   * does not need one: the default's `null` case covers it. So this exists for the type
   * that has not turned up yet.
   *
   * `key` names WHICH draft is being asked about, and is always given — callers reach
   * this through {@link isDraftDirty}, which resolves "the one I meant" to the active
   * column's key when the caller names none. `null` is the "asked about the type as a
   * whole" case: the menu dot only wants to know whether this page holds anything at
   * all. Only an identified type can see it, because only an identified type has no
   * single active draft to fall back to.
   */
  isDirty?: (key: DraftKey | null) => boolean;
  /**
   * What ELSE to clear when this draft is thrown away, beside its buffer.
   *
   * A SIDE EFFECT, not the discard itself. Throwing a draft away is emptying its
   * buffer, and `draftStore.discard` does that straight to the buffer store — a
   * per-type `discard` here meant a policy table reached a sibling store to touch
   * state one layer over, and every entry but one wrote out the same two lines to do
   * it. What a type can genuinely still say is that its user input has a SECOND home,
   * which exactly one type does: `customFormPlayer` keeps its answers in localStorage
   * (§G-bis).
   *
   * Runs with the panel unmounted — the rail's ✕ and the parked-draft paths both
   * reach it from outside the column.
   */
  onDiscard?: (key: DraftKey) => void;
}

/**
 * The key a caller means when it names none: the type's single active `:new`
 * column, which is the only draft of a type that can be referred to without saying
 * which one (ADR-0029 §G).
 *
 * `null` for an identified type, which has no such column — `customFormPlayer`'s
 * drafts are all named by what they are OF, so "the" draft of that type is not a
 * thing that exists. Its `isDirty` reads the `null` as "any of them".
 */
function defaultKeyFor(definition: DraftDefinition): DraftKey | null {
  return definition.identifiedOnly ? null : activeDraftKey(definition.type);
}

/**
 * Does this type's draft hold unsaved work? Naming no `key` asks about the active
 * `:new` column — see {@link defaultKeyFor}.
 *
 * Preferred over reaching for `draftDefinition(type)?.isDirty(...)`: the fallback
 * key is decided here, once, instead of at each of the eight call sites that used
 * to spell it out.
 */
export function isDraftDirty(type: string, key?: DraftKey | null): boolean {
  const definition = draftDefinition(type);
  if (!definition) return false;
  const asked = key ?? defaultKeyFor(definition);
  return definition.isDirty?.(asked) ?? bufferBackedIsDirty(definition.type, asked);
}

/**
 * How a draft that keeps its values in a buffer holds unsaved work — which is every
 * declared type (TM-9455 / ADR-0029 §H-1).
 *
 * THE DEFAULT, not a shared literal: nine entries used to spread the same one-liner in,
 * and a type could still forget to. A declared buffer is now the whole declaration, and
 * `isDirty` on a definition is the exception rather than the rule.
 *
 * `null` asks about the type as a WHOLE — "does THIS PAGE hold any Entwurf of this
 * type?". Answered here for every identified type rather than by the one that happened
 * to need it first: an identified type has no single active draft to fall back to (see
 * {@link defaultKeyFor}), so `null` reaching this is the general shape of that case
 * rather than a quirk of `customFormPlayer`.
 *
 * Only IDENTITY-named drafts count, and that is the page scoping rather than a leftover.
 * A draft named by identity is named by a live column, so it is on the page that named
 * it; a PARKED draft carries an instance key instead and may sit on a page the user left
 * (that is what parking is). Buffer keys hold no route, so this layer cannot tell which
 * page a parked one belongs to — `draftStore` can, and does, by filtering its instances
 * on `routeName`. Counting parked keys here would report work from another page as this
 * page's.
 */
function bufferBackedIsDirty(type: SidebarType, key: DraftKey | null): boolean {
  const dirty = useDraftStateStore().dirtyKeys;
  if (key) return dirty.has(key);
  return [...dirty].some(
    (candidate) => draftKeyType(candidate) === type && draftKeyKind(candidate) === 'identity',
  );
}

export const drafts: DraftDefinition[] = [
  {
    // Its ~30 entered values used to live in a Pinia store, i.e. one form for the whole
    // app. They moved into a buffer (see useOfficeLocationForm) so a page can hold
    // several Standort drafts — a singleton handed them all the same values.
    type: 'addOfficeLocation',
    zone: true,
    create: createAddOfficeLocationBuffer,
    // Registry title already says "Standort erstellen", so no `draftTitle` needed.
  },
  {
    // Not a create flow at all: a questionnaire being filled out for one patient.
    // It is identified (`keyFor`) rather than generational — see that field — and it
    // is the one draft whose values the panel already persists itself, in
    // localStorage keyed by form + patient. So the buffer here holds only what that
    // storage does not: WHICH STEP the user was on, which is the difference between
    // coming back to a half-answered form and starting it again.
    type: 'customFormPlayer',
    zone: true,
    identifiedOnly: true,
    create: createCustomFormPlayerBuffer,
    // Registry title is "Fragebogen", which does not say it is unfinished.
    draftTitle: 'Formular ausfüllen',
    keyFor: (props) => identityDraftKey('customFormPlayer', props.customFormId, props.patientId),
    // No `isDirty`: it used to answer the `null` case here — "any questionnaire on this
    // page" — and that answer generalised unchanged, so it is `bufferBackedIsDirty`'s
    // now. An identified type is exactly the type that can be asked with `null`, because
    // it has no single active column to fall back to.
    //
    // The only discard hook in the registry. The step lives in the buffer
    // above and goes with it; the ANSWERS live in the panel's own localStorage, under
    // the same identity. Clearing only the buffer left them there to be restored by the
    // next player — the discarded draft came back, filled in, and read as unsaved work
    // again the moment it was opened.
    onDiscard: (key) => {
      const [formId, patientId] = identityDraftKeyParts(key);
      clearCustomFormAnswers(formId ?? '', patientId ?? '');
    },
  },
  {
    // Creates AND edits from one panel too. Its staged editor computes dirtiness
    // against a snapshot taken from the buffer's FIRST value, which is what lets a
    // restored draft still read as changed — comparing against a fresh empty form
    // never could, because that generates a new section UUID on every call.
    //
    // BOTH generational and identified. A new form is a create flow — one active
    // `:new` column that graduates (§G). An EDIT of a saved form is not: it is a
    // draft of that form's identity, any number can be open, and none replaces
    // another (§G-bis). Unsaved changes to an existing form are exactly as easy to
    // lose as unsaved changes to a new one, so both get the state.
    type: 'customFormDetail',
    zone: true,
    create: createCustomFormDetailBuffer,
    // Registry title is "Formular bearbeiten"; the create side needs its own name.
    draftTitle: (props) =>
      idKindOf(props.customFormId) === 'record' ? 'Formular bearbeiten' : 'Formular erstellen',
    keyFor: (props) =>
      idKindOf(props.customFormId) === 'record'
        ? identityDraftKey('customFormDetail', props.customFormId)
        : null,
  },
  {
    // Only „Leistung anlegen" is a draft: a saved Leistung saves each value on its own
    // (ADR-0048), so its column never reaches this definition. A picked image is a
    // `File`, which carries no JSON — the projection reduces it to name + size so
    // choosing a picture still counts. No key means the ACTIVE `:new` column.
    type: 'officeProductDetails',
    zone: true,
    create: createOfficeProductBuffer,
    // Registry title is „Leistung", the saved record's; the draft's is its verb phrase.
    draftTitle: 'Leistung anlegen',
  },
  {
    // Fields spread over individual refs rather than one object; the buffer carries them
    // together. The Praxisleitung and the Standort both default from a query, so both
    // are absorbed rather than counted through the seam's `seed` (§B-ter).
    //
    // First type in the draft zone: the patient list can hold several "Patient
    // anlegen" panels open at once, so every question about a draft here has to name
    // WHICH one. No key means the ACTIVE `:new` column — the single create buffer that
    // lives in the URL and graduates into the zone once it holds work.
    type: 'createPatient',
    zone: true,
    create: createPatientBuffer,
  },
  {
    // The smallest of the seven: four fields, no post-baseline seeding — which is
    // why it was the first to follow `createPatient` into the zone. No key means the
    // ACTIVE `:new` column, which keys its state by the bare type.
    type: 'inviteUser',
    zone: true,
    create: createInviteUserBuffer,
  },
  {
    // One `reactive(defaultComposerState())` plus the picked-therapist list, so the
    // buffer mechanism carries it whole. Its ~90 lines of top-level prop seeding are
    // gated on a fresh buffer — re-running them on a restored draft would reset the
    // mode and the time the user had already chosen.
    //
    // The SECOND buffer factory the registry cannot hold (§H-1 names `prescription` as
    // the first). `defaultComposerState` and the ~15 types it returns live under
    // `components/AppointmentComposer/`, and the boundaries fence forbids `stores` from
    // reaching there — deliberately, since it is the layering the buffer store is
    // opaque to preserve. So the composer declares its factory inline, at the seam.
    type: 'appointmentComposer',
    zone: true,
  },
  {
    // The appointment column's create side (#1933, ADR-0046): a `:new` column opened on
    // a calendar slot, which becomes the new appointment's detail column once it is
    // created. The detail side is not an Entwurf: it saves each field on its own.
    //
    // A third factory the registry cannot hold, for the composer's reason: the buffer is
    // the panel's draft, whose types live under `components/`. So the column declares
    // it at the seam.
    type: 'appointment',
    zone: true,
    // Registry title is the detail side's "Termindetails".
    draftTitle: 'Termin erstellen',
  },
  {
    // The one type with SEVERAL buffers under one key, and the reason the variant
    // mechanism exists (ADR-0029 §H-1). Two of them: the wrapper's `flow` — the picker
    // step and the chosen Fall — and the `body` the chosen Fall leads to. The step
    // deliberately does NOT count towards dirtiness: choosing a Fall only decides which
    // body renders, so the Entwurf starts inside that form (the wrapper opts out with
    // the seam's `project: false`).
    //
    // `reset` matches the whole `prescription…/…` family, so the step and the Fall go
    // with the form values. That is what the separate `prescriptionDraftStore` used to
    // be for; it was a singleton, so two Entwürfe would have shared one picker step.
    type: 'prescription',
    zone: true,
    // No `create`: this is the type §H-1 names as the exception. Its buffer shape depends
    // on the payer domain the user picks in the picker, and a column's bound props carry
    // the create sentinel and nothing about the Fall — so the factories stay in the
    // wrapper and the payer bodies, which are the only places that know it.
  },
  {
    // A wizard rather than a form of fields, and the last type to have held its state in
    // a module rather than a buffer — one half-built Übung for the whole application,
    // which outlived a logout and could never be two (ADR-0029 §H-4).
    //
    // Its position, its upload status and the exercise being built are one reactive
    // object, so the ordinary buffer comparison covers the lot — the bespoke `isDirty`
    // it used to carry ("past the first step, or a title, or a file") existed because
    // only the machine could see where the wizard was. The step is now a field like any
    // other, and choosing a Sichtbarkeit reads as the decision it is.
    type: 'addExercise',
    zone: true,
    create: createAddExerciseWizard,
    // Registry title already says "Übung erstellen", so no `draftTitle` needed.
  },
];

export function draftDefinition(type: string): DraftDefinition | undefined {
  return drafts.find((draft) => draft.type === type);
}
